import type { BankCategory } from "./bank-types.js";

/**
 * Themes are the unit teams ban at the start of a match.
 *
 * They are deliberately finer than the 16 topics. With only 16, putting 12 on
 * the board and banning 2 removes about a tenth of the bank and a ban means
 * almost nothing. Split into ~30, banning "Motorsport" or "The Ancient World"
 * actually takes something off the table.
 *
 * Nothing is re-authored to support this: a theme is derived from the category
 * id, topic and tags that already exist.
 */

export interface Theme {
  id: string;
  label: string;
  /** One line shown under the label on the ban board. */
  blurb: string;
}

export const THEMES: Theme[] = [
  {
    id: "countries",
    label: "Countries & Borders",
    blurb: "nations by size, people and neighbours",
  },
  {
    id: "landscapes",
    label: "Rivers, Peaks & Deserts",
    blurb: "the physical world",
  },
  { id: "cities", label: "Cities", blurb: "where everybody lives" },

  {
    id: "football",
    label: "Football",
    blurb: "the round-ball game, everywhere it is played",
  },
  {
    id: "olympics",
    label: "The Olympics",
    blurb: "summer, winter and the medal tables",
  },
  {
    id: "bat-sports",
    label: "Cricket & Bat Sports",
    blurb: "cricket, baseball, hockey",
  },
  {
    id: "motorsport",
    label: "Speed & Endurance",
    blurb: "motorsport, cycling, the long way round",
  },
  {
    id: "court-ring",
    label: "Court & Ring",
    blurb: "tennis, boxing, martial arts, the indoor games",
  },

  {
    id: "human-body",
    label: "The Human Body",
    blurb: "bones, organs and what is inside you",
  },
  {
    id: "medicine",
    label: "Medicine & Life",
    blurb: "disease, genetics, the living world",
  },
  {
    id: "elements",
    label: "Elements & Matter",
    blurb: "the periodic table and what things are made of",
  },
  {
    id: "physics-space",
    label: "Physics & Space",
    blurb: "forces, measurement, planets and stars",
  },

  {
    id: "farming",
    label: "Farm & Harvest",
    blurb: "what the world grows and raises",
  },
  {
    id: "cuisine",
    label: "Dishes & Cuisines",
    blurb: "cooking, eating and drinking",
  },

  {
    id: "ancient",
    label: "The Ancient World",
    blurb: "the first civilisations",
  },
  {
    id: "empires",
    label: "Empires & Dynasties",
    blurb: "who ruled what, and for how long",
  },
  {
    id: "modern-history",
    label: "Modern History",
    blurb: "independence, upheaval and the last two centuries",
  },

  {
    id: "animals",
    label: "Animals",
    blurb: "creatures great, small and alarming",
  },
  {
    id: "wild-places",
    label: "Wild Places & Records",
    blurb: "habitats, extremes and conservation",
  },

  {
    id: "world-music",
    label: "World Music",
    blurb: "scenes and sounds by region",
  },
  {
    id: "charts",
    label: "Charts & Records",
    blurb: "what sold, streamed and won",
  },

  {
    id: "money",
    label: "Money & Companies",
    blurb: "economies, trade and business",
  },
  {
    id: "infrastructure",
    label: "Energy & Infrastructure",
    blurb: "power, mines, ports and big builds",
  },
  {
    id: "nations",
    label: "Nations & Alliances",
    blurb: "governments, unions and treaties",
  },

  { id: "cinema", label: "World Cinema", blurb: "films from everywhere" },
  {
    id: "screen-awards",
    label: "Screen & Awards",
    blurb: "festivals, prizes and television",
  },

  {
    id: "art",
    label: "Art & Architecture",
    blurb: "what people made and built",
  },
  { id: "books", label: "Books & Writers", blurb: "literature the world over" },
  {
    id: "languages",
    label: "Languages & Scripts",
    blurb: "how the world talks and writes",
  },

  {
    id: "computing",
    label: "Tech & Computing",
    blurb: "machines, code and the network",
  },
  {
    id: "online",
    label: "Games & Online",
    blurb: "video games and life on the internet",
  },

  { id: "oddities", label: "Odds & Ends", blurb: "the delightfully strange" },
];

export const THEME_IDS = THEMES.map((t) => t.id);
const THEME_BY_ID = new Map(THEMES.map((t) => [t.id, t]));
export const themeById = (id: string): Theme | undefined => THEME_BY_ID.get(id);

/** Case-insensitive substring test against the category id. */
const has = (id: string, ...needles: string[]) =>
  needles.some((n) => id.includes(n));
const tagged = (c: BankCategory, ...needles: string[]) =>
  (c.tags ?? []).some((t) => needles.some((n) => t.toLowerCase().includes(n)));

/**
 * Assign exactly one theme to a category.
 *
 * Ordered rules: the first match wins, so the specific cases sit above the
 * catch-alls. Category ids carry their authoring batch as a prefix and are
 * descriptive beyond it, which makes them a more reliable signal than tags.
 */
export function themeOf(c: BankCategory): string {
  const id = c.id;
  const topic = c.topic;

  // ---------------------------------------------------------------- sport
  if (topic === "sport") {
    if (id.startsWith("olym-") || tagged(c, "olympic", "paralympic"))
      return "olympics";
    if (id.startsWith("ftbl-") || tagged(c, "football")) return "football";
    if (
      has(
        id,
        "cricket",
        "t20",
        "ipl",
        "test-wicket",
        "test-innings",
        "odi",
        "champions-trophy",
        "baseball",
        "world-series",
        "mlb",
        "hockey",
        "softball",
        "kabaddi",
        "sumo",
      )
    )
      return "bat-sports";
    if (
      has(
        id,
        "f1",
        "formula",
        "motogp",
        "rally",
        "le-mans",
        "isle-of-man",
        "cycl",
        "tour-de",
        "grand-tour",
        "sail",
        "americas-cup",
        "horse",
        "marathon",
        "rowing",
      )
    )
      return "motorsport";
    return "court-ring";
  }

  // -------------------------------------------------------------- science
  if (topic === "science") {
    if (id.startsWith("lsci-")) {
      if (tagged(c, "anatomy", "body", "brain", "blood", "teeth", "bones"))
        return "human-body";
      return "medicine";
    }
    if (tagged(c, "element", "chemistry", "material", "mineral"))
      return "elements";
    return "physics-space";
  }
  if (topic === "space") return "physics-space";

  // ------------------------------------------------------------ geography
  if (topic === "geography") {
    if (id.startsWith("ctry-")) return "countries";
    if (
      id.startsWith("city-") ||
      tagged(c, "cities", "capital", "metro", "airport", "port")
    )
      return "cities";
    if (
      id.startsWith("phys-") ||
      tagged(c, "river", "mountain", "lake", "desert", "island", "volcano")
    )
      return "landscapes";
    if (tagged(c, "climate", "weather", "extreme", "temperature"))
      return "landscapes";
    // Everything left in geography is a fact about countries themselves.
    return "countries";
  }

  // --------------------------------------------------------------- nature
  if (topic === "nature") {
    if (
      tagged(
        c,
        "record",
        "habitat",
        "conservation",
        "forest",
        "reef",
        "ocean",
        "biodiversity",
      )
    )
      return "wild-places";
    if (id.startsWith("phys-")) return "landscapes";
    return "animals";
  }

  // ----------------------------------------------------------------- food
  if (topic === "food") {
    if (
      id.startsWith("agri-") ||
      tagged(c, "crop", "agriculture", "livestock", "producer")
    )
      return "farming";
    return "cuisine";
  }

  // -------------------------------------------------------------- history
  if (topic === "history") {
    if (id.startsWith("modh-")) return "modern-history";
    if (tagged(c, "empire", "dynast", "ruler", "monarch", "conquest"))
      return "empires";
    return "ancient";
  }
  if (topic === "politics") {
    if (id.startsWith("modh-")) return "modern-history";
    return "nations";
  }

  // ---------------------------------------------------------------- music
  if (topic === "music") {
    if (tagged(c, "sales", "chart", "streaming", "award", "record"))
      return "charts";
    return "world-music";
  }

  // ------------------------------------------------------------ film & tv
  if (topic === "tv") return "screen-awards";
  if (topic === "film") {
    if (tagged(c, "oscar", "festival", "cannes", "award", "emmy"))
      return "screen-awards";
    return "cinema";
  }

  // ------------------------------------------------------------- business
  if (topic === "business") {
    if (
      tagged(
        c,
        "energy",
        "mining",
        "infrastructure",
        "transport",
        "rail",
        "aviation",
        "shipping",
      )
    )
      return "infrastructure";
    return "money";
  }

  // ------------------------------------------------------- arts & letters
  if (topic === "arts") {
    if (
      id.startsWith("lang-") ||
      tagged(c, "book", "literature", "poet", "novel", "writer")
    )
      return "books";
    return "art";
  }
  if (topic === "language") return "languages";

  // ------------------------------------------------------------- internet
  if (topic === "internet") {
    if (tagged(c, "gaming", "game", "youtube", "social", "web", "streaming"))
      return "online";
    return "computing";
  }

  return "oddities";
}
